import { useState } from "react";

export const INK_COLORS = [
    "#ffffff", "#000000", "#4b5563", "#c0c0c0", "#fff7d6", "#d2b48c",
    "#e11d2e", "#b91c1c", "#800000", "#f97316", "#f5b800", "#ffe500",
    "#a3e635", "#16a34a", "#15803d", "#064e3b", "#14b8a6", "#06b6d4",
    "#38bdf8", "#2563EB", "#1d4ed8", "#1e3a8a", "#0f172a", "#7c3aed",
    "#581c87", "#d946ef", "#ff2d55", "#ec4899", "#7c3f13", "#ffd1dc",
];

export const hexToCmyk = (hex) => {
    const n = parseInt(String(hex || "#000000").slice(1), 16);
    const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const k = 1 - Math.max(r, g, b);
    const f = (v) => (k >= 1 ? 0 : Math.round(((1 - v - k) / (1 - k)) * 100));
    return { c: f(r), m: f(g), y: f(b), k: Math.round(k * 100) };
};

export const cmykToHex = ({ c, m, y, k }) => {
    const ch = (v) => Math.round(255 * (1 - v / 100) * (1 - k / 100)).toString(16).padStart(2, "0");
    return `#${ch(c)}${ch(m)}${ch(y)}`;
};

/** Print-safe swatches, an any-color picker and CMYK sliders — the values a print shop actually uses. */
export default function InkPicker({ value, onChange, theme, testid }) {
    const [sliders, setSliders] = useState(false);
    const cmyk = hexToCmyk(value);
    const setChannel = (key, v) => onChange(cmykToHex({ ...cmyk, [key]: Number(v) }));
    return (
        <div className="space-y-1.5" data-testid={testid}>
            <div className="flex flex-wrap gap-1">
                {INK_COLORS.map((c) => (
                    <button key={c} type="button" onClick={() => onChange(c)} title={c}
                        style={{ backgroundColor: c, borderColor: value?.toLowerCase() === c.toLowerCase() ? theme?.primary : "#d4d4d8" }}
                        className={`h-5 w-5 rounded-full border-2 transition-transform ${value?.toLowerCase() === c.toLowerCase() ? "scale-110" : ""}`}
                        data-testid={`${testid}-${c.slice(1)}`} />
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-zinc-200 px-2 py-1 font-semibold text-zinc-700 hover:bg-zinc-50">
                    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-4 w-4 cursor-pointer border-0 bg-transparent p-0" data-testid={`${testid}-any`} />
                    Any color
                </label>
                <span className="font-mono uppercase text-zinc-500" data-testid={`${testid}-cmyk`}>
                    C{cmyk.c} M{cmyk.m} Y{cmyk.y} K{cmyk.k}
                </span>
                <button type="button" onClick={() => setSliders((s) => !s)} className="ml-auto font-bold underline" style={{ color: theme?.ink || theme?.primary }} data-testid={`${testid}-toggle-sliders`}>
                    {sliders ? "Hide CMYK" : "CMYK sliders"}
                </button>
            </div>
            {sliders && (
                <div className="grid grid-cols-[auto_1fr_2.5rem] items-center gap-x-2 gap-y-1 rounded-md bg-zinc-50 px-2 py-1.5" data-testid={`${testid}-sliders`}>
                    {[["c", "Cyan", "#00b7eb"], ["m", "Magenta", "#ec008c"], ["y", "Yellow", "#ffef00"], ["k", "Black", "#000000"]].map(([key, label, tone]) => (
                        <div key={key} className="contents">
                            <span className="w-4 font-mono text-[10px] font-bold uppercase" style={{ color: tone === "#ffef00" ? "#a16207" : tone }}>{key}</span>
                            <input type="range" min="0" max="100" value={cmyk[key]} onChange={(e) => setChannel(key, e.target.value)} aria-label={label}
                                className="w-full" style={{ accentColor: tone }} data-testid={`${testid}-slider-${key}`} />
                            <span className="text-right font-mono text-[10px] text-zinc-600">{cmyk[key]}%</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
